import { useCallback, useState } from 'react';

export type BotDockSide = 'left' | 'right';

const STORAGE_KEY = 'botDockSide';

function readStoredSide(): BotDockSide | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'left' || value === 'right' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Which screen edge the chat is docked to.
 *
 * With no saved choice, LTR stays on the right and RTL on the left — the
 * same default the launcher used before this control existed. A click stores
 * the opposite edge so the widget and the drawer share it across reloads.
 */
export default function useBotDockSide(direction: 'ltr' | 'rtl'): {
  side: BotDockSide;
  toggle: () => void;
} {
  const defaultSide: BotDockSide = direction === 'rtl' ? 'left' : 'right';
  const [override, setOverride] = useState<BotDockSide | null>(readStoredSide);

  const side = override ?? defaultSide;

  const toggle = useCallback(() => {
    setOverride((current) => {
      const next: BotDockSide = (current ?? defaultSide) === 'left' ? 'right' : 'left';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private mode can throw; the in-memory side still flips.
      }
      return next;
    });
  }, [defaultSide]);

  return { side, toggle };
}
